/**
 * @file useUserFeedback.js
 * @description 我的反馈：列表分页、提交、详情与深链 feedbackId
 * @module features/user/composables/useUserFeedback
 */

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import {
  fetchFeedbackDetailApi,
  fetchFeedbackPageApi,
  submitFeedbackApi,
} from '@/features/user/api/feedbackApi'

/**
 * @returns {Object} 我的反馈模板绑定
 */
export function useUserFeedback() {
  const route = useRoute()
  const router = useRouter()

  /** @type {import('vue').Ref<'list'|'detail'>} */
  const viewMode = ref('list')
  const list = ref([])
  const accumulatedList = ref([])
  const currentPage = ref(1)
  const _pageSizePc = 10
  const _pageSizeMobile = 8
  const pageSize = ref(10)
  const totalPages = ref(1)
  const totalItems = ref(0)
  const token = ref('')
  const isMobile = ref(false)
  const loading = ref(false)
  const loadingMore = ref(false)
  const detailLoading = ref(false)
  /** @type {import('vue').Ref<import('../api/feedbackApi').WebFeedbackDetail|null>} */
  const detail = ref(null)
  const submitVisible = ref(false)
  const submitLoading = ref(false)
  const feedbackSentinel = ref(null)
  let infiniteObserver = null

  /**
   * 供列表子组件绑定无限滚动哨兵 DOM
   * @param {Element|null} el
   */
  function setFeedbackSentinel(el) {
    feedbackSentinel.value = el
  }

  const submitForm = reactive({
    type: 'BUG',
    content: '',
  })

  const submitRules = {
    type: [{ required: true, message: '请选择反馈类型', trigger: 'change' }],
    content: [
      { required: true, message: '请填写反馈内容', trigger: 'blur' },
      { max: 2000, message: '反馈内容不能超过 2000 字', trigger: 'blur' },
    ],
  }

  const displayList = computed(() =>
    isMobile.value ? accumulatedList.value : list.value
  )
  const listNoMore = computed(() => {
    if (!isMobile.value || totalItems.value === 0) return true
    return accumulatedList.value.length >= totalItems.value
  })
  const showInfiniteEndTip = computed(() => {
    const ps = pageSize.value
    if (totalItems.value <= ps) return false
    if (loading.value || loadingMore.value) return false
    if (displayList.value.length === 0) return false
    if (isMobile.value) return listNoMore.value
    return currentPage.value >= totalPages.value && totalPages.value >= 1
  })

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
    pageSize.value = isMobile.value ? _pageSizeMobile : _pageSizePc
  }

  function unbindInfiniteObserver() {
    if (infiniteObserver) {
      infiniteObserver.disconnect()
      infiniteObserver = null
    }
  }

  function bindInfiniteObserver() {
    unbindInfiniteObserver()
    if (!isMobile.value || typeof IntersectionObserver === 'undefined') return
    const el = feedbackSentinel.value
    if (!el || listNoMore.value || loading.value || loadingMore.value) return
    infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          listNoMore.value ||
          loadingMore.value ||
          loading.value ||
          !isMobile.value
        ) {
          return
        }
        fetchList(true)
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    infiniteObserver.observe(el)
  }

  function resolveRequestPageNum(append) {
    if (append) return currentPage.value + 1
    if (isMobile.value) return 1
    return currentPage.value
  }

  /**
   * @param {import('../api/feedbackApi').WebFeedbackPageItem} item
   */
  function mapListItem(item) {
    return {
      feedbackId: item.feedbackId != null ? String(item.feedbackId) : '',
      type: item.type || '',
      typeName: item.typeName || item.type || '',
      status: item.status || '',
      statusName: item.statusName || item.status || '',
      contentSummary: item.contentSummary || '',
      submitTime: item.submitTime || '',
      replied: Boolean(item.replied),
    }
  }

  /**
   * @param {boolean} [append]
   */
  async function fetchList(append) {
    if (append) {
      if (
        !isMobile.value ||
        loadingMore.value ||
        listNoMore.value ||
        accumulatedList.value.length >= totalItems.value
      ) {
        return
      }
    }
    if (!token.value) {
      console.warn('未找到用户授权信息，请登录后重试')
      return
    }
    const pageNum = resolveRequestPageNum(append)
    if (!append) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    try {
      const response = await fetchFeedbackPageApi({
        pageNum,
        pageSize: pageSize.value,
      })
      if (response.data.code === 200 || response.data.code === 0) {
        const { contents, total } = response.data
        const rows = Array.isArray(contents) ? contents : []
        const mapped = rows.map(mapListItem)
        totalItems.value = total || 0
        totalPages.value = Math.max(
          1,
          Math.ceil(totalItems.value / pageSize.value)
        )
        if (!append) {
          list.value = mapped
          if (isMobile.value) {
            accumulatedList.value = [...mapped]
            currentPage.value = 1
          } else {
            accumulatedList.value = []
          }
        } else if (mapped.length === 0) {
          currentPage.value = pageNum
        } else {
          accumulatedList.value.push(...mapped)
          currentPage.value = pageNum
        }
      } else {
        console.error('获取反馈列表失败:', response.data.msg)
        ElMessage.warning(response.data.msg || '获取反馈列表失败')
      }
    } catch (error) {
      console.error('反馈列表请求错误:', error)
      ElMessage.warning('获取反馈列表失败')
    } finally {
      loading.value = false
      loadingMore.value = false
      nextTick(() => bindInfiniteObserver())
    }
  }

  /**
   * 清除路由中的 feedbackId，避免返回键反复进入详情
   */
  function clearFeedbackIdQuery() {
    if (!route.query.feedbackId) return
    const query = { ...route.query }
    delete query.feedbackId
    if (route.name === 'UserCenter') {
      router.replace({ name: 'UserCenter', query })
    } else if (route.name === 'UserModule') {
      router.replace({
        name: 'UserModule',
        params: { ...route.params },
        query,
      })
    }
  }

  /**
   * @param {string} feedbackId
   * @param {{ clearQuery?: boolean }} [options]
   */
  async function openDetail(feedbackId, options = {}) {
    const id = feedbackId != null ? String(feedbackId).trim() : ''
    if (!id) return
    if (!token.value) {
      ElMessage.warning('请先登录')
      return
    }
    detailLoading.value = true
    try {
      const response = await fetchFeedbackDetailApi({ id })
      if (response.data.code === 200 || response.data.code === 0) {
        const data = response.data.data || {}
        detail.value = {
          feedbackId:
            data.feedbackId != null ? String(data.feedbackId) : id,
          type: data.type || '',
          typeName: data.typeName || data.type || '',
          status: data.status || '',
          statusName: data.statusName || data.status || '',
          content: data.content || '',
          submitTime: data.submitTime || '',
          replyContent: data.replyContent || '',
          replyTime: data.replyTime || '',
        }
        viewMode.value = 'detail'
        if (options.clearQuery !== false) {
          clearFeedbackIdQuery()
        }
      } else {
        ElMessage.warning(response.data.msg || '获取反馈详情失败')
      }
    } catch (error) {
      console.error('反馈详情请求错误:', error)
      ElMessage.warning('获取反馈详情失败')
    } finally {
      detailLoading.value = false
    }
  }

  function backToList() {
    viewMode.value = 'list'
    detail.value = null
  }

  function openSubmitDialog() {
    submitForm.type = 'BUG'
    submitForm.content = ''
    submitVisible.value = true
  }

  function closeSubmitDialog() {
    submitVisible.value = false
  }

  /**
   * @param {import('element-plus').FormInstance|null|undefined} formRef
   */
  async function handleSubmit(formRef) {
    if (!formRef) return
    try {
      await formRef.validate()
    } catch {
      return
    }
    if (!token.value) {
      ElMessage.warning('请先登录')
      return
    }
    submitLoading.value = true
    try {
      const response = await submitFeedbackApi({
        type: submitForm.type,
        content: submitForm.content.trim(),
      })
      if (response.data.code === 200 || response.data.code === 0) {
        ElMessage.success(response.data.msg || '反馈已提交')
        closeSubmitDialog()
        currentPage.value = 1
        accumulatedList.value = []
        await fetchList(false)
      } else {
        ElMessage.warning(response.data.msg || '提交失败')
      }
    } catch (error) {
      console.error('提交反馈失败:', error)
      ElMessage.warning(
        error?.response?.data?.msg || error?.message || '提交失败'
      )
    } finally {
      submitLoading.value = false
    }
  }

  function nextPage() {
    if (isMobile.value) return
    if (currentPage.value < totalPages.value) {
      currentPage.value++
      fetchList(false)
    }
  }

  function previousPage() {
    if (isMobile.value) return
    if (currentPage.value > 1) {
      currentPage.value--
      fetchList(false)
    }
  }

  function onResize() {
    const prev = isMobile.value
    updateIsMobile()
    if (prev !== isMobile.value && viewMode.value === 'list') {
      currentPage.value = 1
      accumulatedList.value = []
      fetchList(false)
    }
  }

  /**
   * 处理深链：有 feedbackId 时自动进入详情
   */
  async function handleDeeplinkFeedbackId() {
    const raw = route.query.feedbackId
    const id = raw != null ? String(raw).trim() : ''
    if (!id) return
    await openDetail(id, { clearQuery: true })
  }

  watch(
    () => route.query.feedbackId,
    (id) => {
      if (id) {
        handleDeeplinkFeedbackId()
      }
    }
  )

  onMounted(() => {
    token.value = JSON.parse(localStorage.getItem('user') || 'null')?.token || ''
    updateIsMobile()
    window.addEventListener('resize', onResize)
    fetchList(false)
    nextTick(() => {
      bindInfiniteObserver()
      handleDeeplinkFeedbackId()
    })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
    unbindInfiniteObserver()
  })

  return {
    viewMode,
    displayList,
    loading,
    loadingMore,
    detailLoading,
    detail,
    isMobile,
    setFeedbackSentinel,
    showInfiniteEndTip,
    currentPage,
    totalPages,
    totalItems,
    submitVisible,
    submitLoading,
    submitForm,
    submitRules,
    openDetail,
    backToList,
    openSubmitDialog,
    closeSubmitDialog,
    handleSubmit,
    nextPage,
    previousPage,
  }
}
